import React, { useState } from 'react';
import { StintInfo } from '../types/strategy';

interface FuelSavingSuggestionsProps {
  stints: StintInfo[];
  fuelCapacity: number;
}

/**
 * Display fuel saving suggestions to complete one extra lap per stint
 * Shows color-coded cards based on difficulty of achieving the savings
 */
const FuelSavingSuggestions: React.FC<FuelSavingSuggestionsProps> = ({ stints, fuelCapacity: _fuelCapacity }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  
  // Filter stints that have suggestions
  const stintsWithSuggestions = stints.filter(stint => stint.extraLapSuggestion);

  // Only show first stint since avg fuel consumption is the same for all stints
  const displayStints = stintsWithSuggestions.length > 0 ? [stintsWithSuggestions[0]] : [];

  // Debug logging
  console.log('FuelSavingSuggestions Debug:', {
    totalStints: stints.length,
    stintsWithSuggestions: stintsWithSuggestions.length,
    stintDetails: stints.map(s => ({
      stint: s.stintNumber,
      laps: s.laps,
      fuelUsed: s.fuelUsed,
      hasSuggestion: !!s.extraLapSuggestion,
      suggestion: s.extraLapSuggestion
    }))
  });

  // Don't show anything if no suggestions
  if (stintsWithSuggestions.length === 0) {
    return null;
  }

  // Color scheme based on severity (dark theme to match StrategyResult)
  const getSeverityColors = (severity: 'easy' | 'moderate' | 'hard' | 'impossible') => {
    switch (severity) {
      case 'easy':
        return {
          bg: 'bg-green-900/20',
          border: 'border-green-500',
          text: 'text-green-400',
          icon: '💡'
        };
      case 'moderate':
        return {
          bg: 'bg-yellow-900/20',
          border: 'border-yellow-500',
          text: 'text-yellow-400',
          icon: '⚠️'
        };
      case 'hard':
        return {
          bg: 'bg-orange-900/20',
          border: 'border-orange-500',
          text: 'text-orange-400',
          icon: '🔥'
        };
      case 'impossible':
      default:
        return {
          bg: 'bg-red-900/20',
          border: 'border-red-500',
          text: 'text-red-400',
          icon: '❌'
        };
    }
  };

  return (
    <div className="bg-slate-800 p-6 rounded-xl border border-slate-700 mb-6">
      {/* Title - clickable to collapse/expand */}
      <div 
        className="flex items-center justify-between cursor-pointer select-none"
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        <h2 className="text-xl font-semibold text-white">💡 Fuel Saving Opportunities</h2>
        <span className={`text-slate-400 transition-transform duration-200 text-sm ${isCollapsed ? '' : 'rotate-180'}`}>▼</span>
      </div>
      
      {!isCollapsed && <>
      <p className="text-sm text-slate-400 mb-4 mt-2">
        Small fuel savings per lap could allow you to complete one extra lap before pitting, potentially reducing total pit stops.
      </p>
      
      <div className="space-y-4">
        {displayStints.map((stint) => {
          const suggestion = stint.extraLapSuggestion!;
          const colors = getSeverityColors(suggestion.severity);

          return (
            <div
              key={stint.stintNumber}
              className={`p-4 rounded-lg border ${colors.bg} ${colors.border}`}
            >
              <div className="flex items-start justify-between mb-2">
                <h4 className={`font-semibold ${colors.text}`}>
                  +1 Lap Per Stint ({stint.laps} → {stint.laps + 1} laps)
                </h4>
                <span className="text-2xl">{colors.icon}</span>
              </div>

              <p className={`text-sm mb-3 text-slate-300`}>
                {suggestion.message}
              </p>

              <div className={`grid grid-cols-2 gap-2 text-xs text-slate-400`}>
                <div>
                  <span className="font-medium">Fuel Short:</span>
                  <br />
                  <span className={colors.text}>{suggestion.fuelShortfall.toFixed(3)}L</span>
                </div>
                <div>
                  <span className="font-medium">Save Per Lap:</span>
                  <br />
                  <span className={colors.text}>{suggestion.fuelSavePerLap.toFixed(3)}L</span>
                </div>
                <div>
                  <span className="font-medium">Target Fuel/Lap:</span>
                  <br />
                  <span className={colors.text}>{((stint.fuelUsed / stint.laps) - suggestion.fuelSavePerLap).toFixed(2)}L</span>
                </div>
                <div>
                  <span className="font-medium">Pit Time Saved:</span>
                  <br />
                  <span className={colors.text}>{suggestion.pitTimeSaved.toFixed(1)}s</span>
                </div>
                <div>
                  <span className="font-medium">Percentage:</span>
                  <br />
                  <span className={colors.text}>{suggestion.fuelSavePercentage.toFixed(2)}%</span>
                </div>
                <div>
                  <span className="font-medium">Difficulty:</span>
                  <br />
                  <span className={colors.text}>{suggestion.severity.charAt(0).toUpperCase() + suggestion.severity.slice(1)}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      </>}
    </div>
  );
};

export default FuelSavingSuggestions;
